import React, { useEffect, useState } from 'react';
import { Form } from '@ant-design/compatible';
import cx from 'classnames';
import produce from 'immer';
import { MdKeyboardArrowRight } from 'react-icons/md';

import ProductDetailPanel from '../../ProductDetailPanel';
import InputForm from '../../../../../components/form/InputForm';
import useFormState from '../../../../../hooks/product/useFormState';
import useSelectCategoryMutate from '../../../../../hooks/queries/product/useSelectCategoryMutate';
import { compareProductVersion } from '../../../../../lib/utils';
import { formRequiredRules } from '../../../../../lib/formRules';
import normalCategoryIcon from '../../../../../static/images/product/productDetail/normal-category-icon.png';
import emphasisCategoryIcon from '../../../../../static/images/product/productDetail/emphasis-category-icon.png';
import { PRODUCT_DETAIL_PAGE_PANEL_NAME } from '../../../../../constants/service/app/productConstants';
import { REQUIRED_MSG } from '../../../../../constants/service/common/formRulesConstants';
import './ProductCategory.scss';

const CategorySelector = (props) => {
  const { title, list = [], selectedList, activeId, isMain, onClick } = props;
  return (
    <div className="category-selector">
      <div className={cx('category-selector-title', { required: isMain })}>{title}</div>
      <ul className="category-selector-content">
        {list.map((item) => {
          const normal = item.type === 0;
          const emphasis = item.type === 1;
          const selected = selectedList?.find((target) => target.id === item.id);
          const disabled = isMain && !normal && selectedList.length === 0;
          const active = item.id === activeId;
          return (
            <li
              key={item.id}
              className={cx('category-selector-item', isMain ? 'main' : 'sub', {
                normal,
                emphasis,
                selected,
                disabled,
                active,
              })}
              onClick={disabled ? () => {} : () => onClick(item)}
            >
              <span>
                {normal ? <img src={normalCategoryIcon} /> : null}
                {emphasis ? <img src={emphasisCategoryIcon} /> : null}
                {item.name}
              </span>
              {isMain && <MdKeyboardArrowRight className="arrow" />}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const ProductCategory = (props) => {
  const {
    isEditable,
    form,
    initialValue,
    mainProductCategorySelectList,
    subProductCategorySelectList,
    isCompare,
    comparisonData,
    ...res
  } = props;
  const { getFieldDecorator } = form;
  const [selectedCategoryList, setSelectedCategoryList] = useFormState(initialValue.categoryList, {
    form,
    key: 'categoryList',
  });
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const selectCategoryMutate = useSelectCategoryMutate();

  /**
   * 첫번째 선택된 카테고리에 맞춰 상품 최소 금액, 첫번째 선택된 카테고리 id 값 변경
   */
  useEffect(() => {
    selectCategoryMutate.mutate(selectedCategoryList[0]?.name, {});
  }, [selectedCategoryList[0]]);

  /**
   *
   */
  useEffect(() => {
    if (selectedCategoryList.length > 0) return;
    form.validateFields(['categoryList'], () => {
      return;
    });
  }, [selectedCategoryList]);

  const addMainCategory = (item) => {
    setSelectedMainCategory(item.id);

    // 이미 선택된 카테고리일 경우 return
    if (selectedCategoryList.find((target) => target.id === item.id)) return;

    setSelectedCategoryList((prev) => [
      ...prev,
      {
        id: item.id,
        name: item.name,
        productCategoryUpdateLogId: item.productCategoryUpdateLogId,
        subCategory: [],
      },
    ]);
  };

  const addSubCategory = (item) => {
    setSelectedCategoryList((prev) => {
      return produce(prev, (draft) => {
        const target = draft.find((target) => target.id === selectedMainCategory);
        if (target.subCategory.find((target) => target.id === item.id)) return;
        target.subCategory.push({
          id: item.id,
          name: item.name,
          productCategoryUpdateLogId: item.productCategoryUpdateLogId,
        });
      });
    });
  };

  const deleteCategory = async (mainIndex, subIndex) => {
    setSelectedCategoryList((prev) => {
      return produce(prev, (draft) => {
        const target = draft[mainIndex];
        if (target.subCategory.length > 1) {
          target.subCategory.splice(subIndex, 1);
          return;
        }
        draft.splice(mainIndex, 1);

        /**
         * 선택한 1차 카테고리를 삭제했을 때 2차 카테고리 노출 안되게 하기위해
         * selectedMainCategory null 처리
         */
        if (target.id === selectedMainCategory) {
          setSelectedMainCategory(null);
        }
      });
    });
  };

  return (
    <ProductDetailPanel
      name={PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_CATEGORY}
      className="product-category"
      title="카테고리"
      isCompare={isCompare}
      {...res}
    >
      <div className="panel-body">
        <div>
          {isEditable && (
            <>
              <div className="category-guide">
                <div>✽ 사용자 화면에 노출 될 카테고리 정보를 입력해 주세요 (중복 선택 가능)</div>
                <div>
                  ✽ 강조형 카테고리를 첫번째 순서로 설정 시 아몬즈원 에서는 판매자가 카테고리 노출 정보를 수정 할 수
                  없는 문제가 발생 됩니다.
                </div>
                <div>✽ 일반형 카테고리를 먼저 설정 해주세요</div>
              </div>
              <Form.Item className={cx('category-form', 'amondz-form')}>
                {getFieldDecorator('categoryList', {
                  initialValue: selectedCategoryList,
                  rules: [formRequiredRules(true, REQUIRED_MSG, false)],
                })(
                  <div className="category-wrapper">
                    <CategorySelector
                      title="1차 카테고리"
                      list={[...mainProductCategorySelectList.normal, ...mainProductCategorySelectList.emphasis]}
                      selectedList={selectedCategoryList}
                      activeId={selectedMainCategory}
                      isMain
                      onClick={addMainCategory}
                    />
                    {selectedMainCategory ? (
                      <CategorySelector
                        title="2차 카테고리"
                        list={subProductCategorySelectList[selectedMainCategory]}
                        selectedList={
                          selectedCategoryList?.find((target) => target.id === selectedMainCategory)?.subCategory
                        }
                        onClick={addSubCategory}
                      />
                    ) : null}
                  </div>,
                )}
              </Form.Item>
            </>
          )}

          <div
            className={cx(
              'category-select-result',
              isCompare && compareProductVersion(initialValue.categoryList, comparisonData.categoryList),
            )}
          >
            <div className="category-select-result-title label">카테고리 선택 결과:</div>
            <ol className="category-select-result-title-content">
              {selectedCategoryList?.map((main, mainIndex) => (
                <li key={main.id} className="main-category">
                  <span className="category-name">{`${mainIndex + 1}. ${main.name}`}</span>
                  <ul>
                    {main.subCategory.length > 0 ? (
                      main.subCategory.map((sub, subIndex) => (
                        <li key={sub.id} className="sub-category">
                          <MdKeyboardArrowRight className="arrow" />
                          <span className="category-name">{sub.name}</span>
                          {isEditable ? (
                            <button
                              className="delete-btn"
                              type="button"
                              onClick={() => deleteCategory(mainIndex, subIndex)}
                            >
                              X
                            </button>
                          ) : null}
                        </li>
                      ))
                    ) : (
                      <li className="sub-category">
                        {isEditable ? (
                          <button className="delete-btn" type="button" onClick={() => deleteCategory(mainIndex)}>
                            X
                          </button>
                        ) : null}
                      </li>
                    )}
                  </ul>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
      {!isEditable && (
        <div className="hidden-form">
          <InputForm form={form} type="hidden" name="categoryList" initialValue={selectedCategoryList} />
        </div>
      )}
    </ProductDetailPanel>
  );
};

export default ProductCategory;
